/**
 * Display-only FX for showing GBP membership amounts in another currency (e.g. EUR).
 * Charges remain in GBP / event currency on Stripe — this does not affect billing.
 *
 * Rates come from our API (`ExchangeRate/gbp-to`), which proxies Frankfurter (ECB) so
 * the browser never calls a third-party FX URL (avoids CORS/ad-block issues).
 * @see https://www.frankfurter.app/
 */

import axiosInstance, { getAPIURL } from "../api/axiosInstance";

const CACHE_MS = 60 * 60 * 1000; // 1 hour
const CACHE_PREFIX = "smbf_fx_gbp_";

export type GbpToCurrencyResult = {
  /** Multiply GBP amount by this to get amount in `targetCurrency`. */
  rate: number;
  /** Rate date from ECB (YYYY-MM-DD). */
  date: string;
};

function cacheKey(targetUpper: string): string {
  return `${CACHE_PREFIX}${targetUpper}`;
}

function readCache(targetUpper: string): GbpToCurrencyResult | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(targetUpper));
    if (!raw) return null;
    const p = JSON.parse(raw) as {
      rate: number;
      date: string;
      at: number;
    };
    if (
      typeof p.rate !== "number" ||
      typeof p.date !== "string" ||
      typeof p.at !== "number"
    )
      return null;
    if (Date.now() - p.at > CACHE_MS) return null;
    return { rate: p.rate, date: p.date };
  } catch {
    return null;
  }
}

function writeCache(targetUpper: string, result: GbpToCurrencyResult): void {
  try {
    sessionStorage.setItem(
      cacheKey(targetUpper),
      JSON.stringify({ ...result, at: Date.now() }),
    );
  } catch {
    /* ignore quota */
  }
}

/**
 * Returns how many units of `targetCurrency` equal 1 GBP (e.g. EUR → ~1.17).
 */
export async function fetchGbpToCurrency(
  targetCurrency: string,
): Promise<GbpToCurrencyResult> {
  const t = (targetCurrency || "").trim().toUpperCase();
  if (t === "GBP") {
    return { rate: 1, date: new Date().toISOString().slice(0, 10) };
  }
  if (t.length !== 3) {
    throw new Error("Invalid currency code.");
  }

  const cached = readCache(t);
  if (cached) return cached;

  const { data } = await axiosInstance.get<{
    rate: number;
    date: string;
  }>(
    getAPIURL(
      `/ExchangeRate/gbp-to?currency=${encodeURIComponent(t)}`,
    ),
  );

  const rate = data.rate;
  if (typeof rate !== "number" || !Number.isFinite(rate)) {
    throw new Error(`No ${t} rate returned.`);
  }
  const date =
    typeof data.date === "string" && data.date.length >= 10
      ? data.date.slice(0, 10)
      : new Date().toISOString().slice(0, 10);

  const result: GbpToCurrencyResult = { rate, date };
  writeCache(t, result);
  return result;
}

export function convertGbpAmountMajor(
  amountGbp: number,
  gbpToTargetRate: number,
): number {
  return Math.round(amountGbp * gbpToTargetRate * 100) / 100;
}
